// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import React, {Component} from 'react';
import MessagingBoxScss from './MessagingBox.scss';
import CCDUtil from '../../../../utils/CCDUtil';
import AppsUtil from '../../../../utils/AppsUtil';
import {Toast} from '@react/react-spectrum/Toast';
import CCDIngestAnalytics from '../../../../analytics/CCDIngestAnalytics';
import {blueBoxClickData, getContextGuid, msgToastData} from '../../../../utils/CCDAnalyticsUtils';
import {analytics} from '../../../../constants/ccdConstants';
import Link from '@react/react-spectrum/Link';

type Props = {
	environment: string,
	messageCode: string,
	locale: string,
	formatMessage: Function,
	productDetails: Object,
	genericPDPAnalyticEvent?: ?Function,
	hasThor?: ?boolean,
	isCCH: ?boolean,
	key?: string
};

export default class MessagingBox extends Component<Props> {
	productDetails: Object;
	genericPDPAnalyticEvent: ?Function;

	constructor(props: Props) {
		super(props);
		this.productDetails = this.props.productDetails;
		this.genericPDPAnalyticEvent = this.props.genericPDPAnalyticEvent;
	}

	componentDidMount() {
		this.sendAnalytic(analytics.toastRender);
	}

	sendAnalytic(type: string) {
		const ccdIngestAnalytics = new CCDIngestAnalytics(this.props.environment);
		ccdIngestAnalytics.initAnalytics();

		let data = CCDUtil.getCloneObject(blueBoxClickData);
		switch (type) {
			case 'systemRequirementsClicked':
				data.subType = 'system-requirements';
				data.contextGuid = getContextGuid(true, this.props.hasThor, true);
				break;
			case 'learnMoreMuseClicked' :
				data.subType = 'learn-more';
				break;
			case analytics.toastRender :
				data = CCDUtil.getCloneObject(msgToastData);
				data.value = this.getMessageText();
				break;
			case analytics.toastCloseClick :
				data = CCDUtil.getCloneObject(msgToastData);
				data.type = 'click';
				data.subType = 'toast-close';
				data.value = this.getMessageText();
				break;
		}
		if (this.props.isCCH) {
			data.workflow = 'Apps';
		}
		data.productId = this.props.productDetails.id;
		data.componentName = this.props.hasThor ? 'Thor' : 'Kaizen';
		ccdIngestAnalytics.genericAnalyticEvent(data);
	}

	getSystemReqURL() {
		return AppsUtil.getCCDGoURL(this.props.locale, AppsUtil.getSystemReqURL(this.props.productDetails.id));
	}

	getFormatMessage(id, appName, href, onClick) {
		return this.props.formatMessage({id}, {
			appName,
			a: str => <Link onClick={onClick} href={href} target='_blank'>{str}</Link>,
			br: () => <br/>
		});
	}

	// Get specific message to shown in bluebox according to different cases
	getMessageText() {
		const appName = this.productDetails.name;
		switch (this.props.messageCode) {
			case 'win10Message':
				return this.props.formatMessage({id: 'ccd.old.messagingBox.win10Message'}, {appName: this.productDetails.name});
			case 'olderVersionMessage' :
				if (this.props.hasThor) {
					return this.getFormatMessage('ccd.messagingBox.thorPresent.olderVersionMessage', appName, this.getSystemReqURL(), () => this.sendAnalytic(analytics.systemRequirementsClicked));
				}
				return this.getFormatMessage('ccd.messagingBox.thorAbsent.olderVersionMessage', appName, this.getSystemReqURL(), () => this.sendAnalytic(analytics.systemRequirementsClicked));
			case 'customMessageMuse':
				return this.getFormatMessage('ccd.messagingBox.customMessageMuse', appName, AppsUtil.getCCDGoURL(this.props.locale, 'https://www.adobe.com/go/muse_eol_message'), () => this.sendAnalytic(analytics.learnMoreMuseClicked));
			case '32BitMessage' :
				return this.props.formatMessage({id: 'ccd.old.messagingBox.32BitMessage'}, {appName: this.productDetails.name});
		}
	}

	hideToastMessage() {
		const toastMessage = document.querySelector(`.${MessagingBoxScss.messageContainer}`);
		if (toastMessage) {
			toastMessage.style.display = 'none';
		}
		this.sendAnalytic(analytics.toastCloseClick);
	}

	render() {
		return (
			<div className={MessagingBoxScss.messageContainer}>
				<Toast closable onClose={() => {this.hideToastMessage();}} variant='info'>
					<div className={MessagingBoxScss.messageText}>
						<span>{this.getMessageText()}</span>
					</div>
				</Toast>
			</div>
		);
	}
}
